import { Product, ProductCategory } from '../types/product';
import { products, getProductById, getProductsByCategory, getFeaturedProducts } from '../data/products';

// Simulated API delay for realistic behavior
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const productService = {
  // Get all products with optional filtering
  async getAllProducts(filters?: {
    category?: ProductCategory;
    search?: string;
    organic?: boolean;
    minPrice?: number;
    maxPrice?: number;
    sortBy?: 'price-asc' | 'price-desc' | 'rating' | 'newest';
  }): Promise<Product[]> {
    await delay(300);
    
    let result = [...products];
    
    if (filters) {
      if (filters.category) {
        result = result.filter(p => p.category === filters.category);
      }
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        result = result.filter(p => 
          p.name.toLowerCase().includes(searchLower) ||
          p.description.toLowerCase().includes(searchLower)
        );
      }
      
      if (filters.organic) {
        result = result.filter(p => p.organic);
      }
      
      if (filters.minPrice !== undefined) {
        result = result.filter(p => p.price >= filters.minPrice!);
      }
      
      if (filters.maxPrice !== undefined) {
        result = result.filter(p => p.price <= filters.maxPrice!);
      }
      
      if (filters.sortBy) {
        switch (filters.sortBy) {
          case 'price-asc':
            result.sort((a, b) => a.price - b.price);
            break;
          case 'price-desc':
            result.sort((a, b) => b.price - a.price);
            break;
          case 'rating':
            result.sort((a, b) => b.rating - a.rating);
            break;
          case 'newest':
            result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            break;
        }
      }
    }
    
    return result;
  },

  // Get single product by ID
  getProductById(id: string): Product | null {
    return getProductById(id) || null;
  },

  // Get products by category
  async getProductsByCategory(category: ProductCategory): Promise<Product[]> {
    await delay(300);
    return getProductsByCategory(category);
  },

  // Get featured products
  async getFeaturedProducts(): Promise<Product[]> {
    await delay(200);
    return getFeaturedProducts();
  },

  // Get product categories
  async getCategories(): Promise<ProductCategory[]> {
    await delay(100);
    return [
      'vegetables',
      'fruits',
      'grains',
      'dairy',
      'poultry',
      'herbs',
      'legumes',
      'seeds',
    ];
  },

  // Search products
  async searchProducts(query: string): Promise<Product[]> {
    await delay(300);
    const searchLower = query.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(searchLower) ||
      p.description.toLowerCase().includes(searchLower) ||
      p.category.toLowerCase().includes(searchLower)
    );
  },
};

export default productService;

